import React, { useState } from 'react';
import { ChevronLeft, Mail, Phone, MapPin, Clock, ChevronDown, Send } from 'lucide-react';

export default function Kontakt({ onBack }) {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqItems = [
    {
      question: 'Welche Zahlungsmethoden akzeptiert ihr?',
      answer: 'Wir akzeptieren SEPA-Überweisungen, Kreditkarten und PayPal. Für größere Projekte bieten wir auch flexible Zahlungspläne an.'
    },
    {
      question: 'Wie schnell bekomme ich eine Antwort?',
      answer: 'Während unserer Geschäftszeiten (Mo-Fr 8-18 Uhr) antworten wir meist innerhalb von 2-4 Stunden. Außerhalb dieser Zeiten bekommst du am nächsten Werktag eine Antwort.'
    },
    {
      question: 'Bietet ihr auch Schulungen an?',
      answer: 'Ja, nach der Einführung deiner neuen Web-App können wir dein Team schulen. Das ist optional und wird individuell abgestimmt.'
    },
    {
      question: 'Können die Apps mobil genutzt werden?',
      answer: 'Alle unsere Apps sind vollständig responsiv und funktionieren perfekt auf Smartphone, Tablet und Desktop. Teilweise haben wir auch native Apps im App Store.'
    },
    {
      question: 'Wie sieht es mit Datensicherheit aus?',
      answer: 'Wir nutzen German Hosting mit SSL/TLS-Verschlüsselung. Alle Daten werden täglich gesichert. GDPR-Konformität ist Standard. Für Enterprises bieten wir auch zusätzliche Sicherheitsfeatures.'
    },
    {
      question: 'Kann ich die Apps kostenlos testen?',
      answer: 'Ja! Alle Apps können kostenlos getestet werden. Es gibt keine versteckten Gebühren. Premium-Features und größere Nutzung haben dann faire Preise.'
    }
  ];

  const contactOptions = [
    {
      icon: '💬',
      title: 'Live Chat',
      description: 'Sofortige Antworten zu schnellen Fragen',
      detail: 'Mo-Fr 8:00-18:00 Uhr',
      action: 'Chat starten',
      link: '#'
    },
    {
      icon: '📧',
      title: 'E-Mail Support',
      description: 'Detaillierte Anfragen mit Screenshots',
      detail: 'info@it-seevetal.de',
      action: 'E-Mail schreiben',
      link: 'mailto:info@it-seevetal.de'
    },
    {
      icon: '☎️',
      title: 'Telefon',
      description: 'Für dringende Fälle oder komplexe Themen',
      detail: '+49 4105 6925100',
      action: 'Anrufen',
      link: 'tel:+494105692510'
    },
    {
      icon: '🎫',
      title: 'Ticketsystem',
      description: 'Für Projekte und längerfristige Anfragen',
      detail: 'Support-Portal',
      action: 'Ticket erstellen',
      link: '#'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-b from-slate-50 to-white border-b border-gray-200 py-6">
        <div className="max-w-6xl mx-auto px-6">
          <button onClick={onBack} className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-4 cursor-pointer">
            <ChevronLeft className="w-4 h-4" />
            Zurück zur Startseite
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Kontakt & Support</h1>
          <p className="text-lg text-gray-600 mt-2">Wir sind für dich da – wähle die beste Option für dein Anliegen</p>
        </div>
      </header>

      <main className="py-16 px-6">
        {/* Contact Options */}
        <section className="max-w-6xl mx-auto mb-20">
          <div className="grid md:grid-cols-4 gap-4">
            {contactOptions.map((option, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition">
                <p className="text-4xl mb-4">{option.icon}</p>
                <h3 className="font-bold text-gray-900 mb-2">{option.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{option.description}</p>
                <p className="text-sm font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded mb-4">{option.detail}</p>
                <a href={option.link} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  {option.action} →
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="max-w-2xl mx-auto mb-20">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Schreib uns eine Nachricht</h2>
            <p className="text-gray-600 mb-8">
              Egal ob Frage, Feature-Anfrage oder Feedback – wir freuen uns von dir zu hören!
            </p>

            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Name</label>
                  <input
                    type="text"
                    placeholder="Max Mustermann"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">E-Mail</label>
                  <input
                    type="email"
                    placeholder="max@beispiel.de"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Firma (optional)</label>
                <input
                  type="text"
                  placeholder="Deine Firma"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Anfrage-Typ</label>
                <select className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                  <option>Support für bestehende App</option>
                  <option>Feature-Anfrage</option>
                  <option>Individuelle Webapp</option>
                  <option>Allgemeine Frage</option>
                  <option>Feedback</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Nachricht</label>
                <textarea
                  placeholder="Erzähl uns, wie wir dir helfen können..."
                  rows={6}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg hover:shadow-xl transition font-bold flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                Nachricht senden
              </button>
            </form>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Häufig gestellte Fragen</h2>
          <p className="text-gray-600 mb-8">
            Du findest deine Antwort nicht? Schreib uns gerne eine Nachricht!
          </p>

          <div className="space-y-3">
            {faqItems.map((item, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-start justify-between gap-4 hover:bg-gray-50 transition"
                >
                  <span className="text-left font-medium text-gray-900">{item.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition transform ${
                      expandedFaq === idx ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedFaq === idx && (
                  <div className="px-6 pb-4 text-gray-600 border-t border-gray-200 pt-4">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Office Hours */}
        <section className="max-w-3xl mx-auto bg-gray-50 border border-gray-200 rounded-2xl p-8 mb-20">
          <div className="flex items-start gap-4">
            <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Unsere Support-Zeiten</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-gray-300">
                  <span className="text-gray-700">Montag – Freitag</span>
                  <span className="font-mono font-bold text-gray-900">8:00 – 18:00 Uhr</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-300">
                  <span className="text-gray-700">Samstag</span>
                  <span className="font-mono font-bold text-gray-900">10:00 – 14:00 Uhr</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Sonntag & Feiertage</span>
                  <span className="font-mono font-bold text-gray-900">Nur Notfälle</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mt-6">
                ⚡ <strong>Chat & E-Mails</strong> werden prioritär beantwortet. Für dringende Fälle kannst du uns jederzeit anrufen.
              </p>
            </div>
          </div>
        </section>

        {/* Service Info */}
        <section className="max-w-3xl mx-auto bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Premium Support für Enterprise-Kunden</h3>
          <p className="text-gray-700 mb-4">
            Du brauchst 24/7 Support, einen dedizierten Account-Manager oder SLA-Garantien? Kein Problem!
          </p>
          <p className="text-gray-700 mb-6">
            Für größere Organisationen bieten wir individuell zugeschnittene Support-Pakete an. Dazu gehören:
          </p>
          
          <ul className="space-y-2 text-gray-700 mb-6">
            <li className="flex items-start gap-2">
              <span className="text-purple-600 font-bold">✓</span>
              <span>24/7 Premium Support mit garantierter Antwortzeit</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-600 font-bold">✓</span>
              <span>Dedizierten Account Manager</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-600 font-bold">✓</span>
              <span>Priorisierte Feature-Entwicklung</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-600 font-bold">✓</span>
              <span>Custom SLA mit Uptime-Garantie</span>
            </li>
          </ul>

          <a href="#" className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium">
            Enterprise Support anfragen →
          </a>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6 mt-24">
        <div className="max-w-6xl mx-auto text-center text-sm">
          <p>© 2026 IT-Seevetal. Alle Rechte vorbehalten.</p>
          <div className="flex gap-6 justify-center mt-4 text-xs">
            <a href="/impressum" className="hover:text-white transition">Impressum</a>
            <a href="/datenschutz" className="hover:text-white transition">Datenschutz</a>
            <a href="/" className="hover:text-white transition">Zur Startseite</a>
          </div>
        </div>
      </footer>
    </div>
  );
}